import { PVSiteData, GraphNode, GraphLink, ComponentColors } from '../types';

// Electrical Symbols (Paths centered at 0,0)
// Based on IEC 60617 / IEEE 315 standards with consistent design language
export const SYMBOLS = {
    // === GRID & UTILITY ===
    // Grid connection: AC waveform (sine wave) - represents utility connection point
    grid: "M-15,0 L-12,0 Q-9,-8 -6,0 Q-3,8 0,0 Q3,-8 6,0 Q9,8 12,0 L15,0 M0,-15 L0,-10 M-3,-12 L0,-15 L3,-12",

    // === TRANSFORMERS ===
    // Transformer: Two coupled coils (IEC standard) with magnetic core indication
    transformer: "M-14,-12 L-14,12 M-14,-12 Q-6,-12 -6,-6 Q-6,0 -14,0 Q-6,0 -6,6 Q-6,12 -14,12 M14,-12 L14,12 M14,-12 Q6,-12 6,-6 Q6,0 14,0 Q6,0 6,6 Q6,12 14,12 M-2,-14 L-2,14 M2,-14 L2,14",

    // Control transformer: Smaller dual winding
    control_transformer: "M-10,-8 L-10,0 L-4,0 L-4,-8 L-10,-8 M-10,0 L-10,8 L-4,8 L-4,0 M-7,0 L-7,0 M4,-8 L4,0 L10,0 L10,-8 L4,-8 M4,0 L4,8 L10,8 L10,0 M0,-10 L0,10",

    // Current transformer: Conductor through rectangular core (CT)
    current_transformer: "M0,-16 L0,16 M-12,-8 L-12,8 L-4,8 L-4,-8 L-12,-8 M4,-8 L4,8 L12,8 L12,-8 L4,-8",

    // Voltage transformer: Two windings with connection dots
    voltage_transformer: "M-12,-10 L-12,10 L-6,10 L-6,-10 L-12,-10 M-9,-6 L-9,6 M6,-10 L6,10 L12,10 L12,-10 L6,-10 M9,-6 L9,6 M-3,-4 L3,-4 M-2,0 L2,0",

    // === POWER CONVERSION ===
    // Inverter: Box with DC input, AC output sine wave (existing, unchanged per request)
    inverter: "M-15,-15 L15,-15 L15,15 L-15,15 Z M-15,-15 L15,15 M-10,5 Q-5,15 0,5 T10,5",

    // PCS Bidirectional: AC/DC converter with bidirectional arrows (IEC rectifier/inverter symbol)
    pcs_bidirectional: "M-15,-12 L15,-12 L15,12 L-15,12 Z M-12,0 L-4,0 M-8,-4 L-4,0 L-8,4 M4,0 L12,0 M8,-4 L12,0 L8,4 M-10,-8 Q-7,-5 -4,-8 Q-1,-5 2,-8 M4,6 L4,-2 L10,-2 L10,6",

    // MPPT: Maximum Power Point Tracker - sun symbol with arrow to peak
    mppt: "M-14,-12 L14,-12 L14,12 L-14,12 Z M-10,0 L-2,0 L0,-6 L2,0 L10,0 M-8,-8 L-4,-4 M8,-8 L4,-4 M0,-10 L0,-6 M-6,4 L-6,8 L6,8 L6,4",

    // DC Optimizer: DC symbol with optimization curve
    dc_optimizer: "M-12,-10 L12,-10 L12,10 L-12,10 Z M-8,-4 L-8,4 M-6,-2 L-6,2 M2,-6 L2,6 L8,6 M2,0 L6,0",

    // Microinverter: Small inverter symbol with AC output
    microinverter: "M-10,-8 L10,-8 L10,8 L-10,8 Z M-6,2 Q-3,6 0,2 Q3,-2 6,2 M-6,-4 L-6,0 L-2,0 L-2,-4 M2,-4 L6,-4",

    // === PROTECTION DEVICES ===
    // Circuit breaker: IEC symbol with trip mechanism indication
    breaker: "M-12,-12 L12,-12 L12,12 L-12,12 Z M-8,0 L0,0 L6,-8 M0,0 L0,8 M-8,8 L8,8",

    // Fuse: IEC fuse symbol with fusible element
    fuse: "M-15,0 L-8,0 M-8,-5 L8,-5 L8,5 L-8,5 Z M8,0 L15,0 M-4,0 L4,0",

    // Disconnect switch: IEC isolator/disconnector symbol
    switch: "M-15,0 L-5,0 M-5,0 L8,-10 M8,0 L15,0 M8,-2 L8,2",

    // Protective relay: Rectangle with coil symbol (IEC relay)
    protective_relay: "M-12,-10 L12,-10 L12,10 L-12,10 Z M-8,-4 A2,2 0 1,1 -4,-4 A2,2 0 1,1 0,-4 A2,2 0 1,1 4,-4 M-6,2 L-6,6 M6,2 L6,6 M-6,6 L6,6",

    // Surge protection device: Spark gap with ground (IEC SPD symbol)
    surge_protection_device: "M-8,-12 L-8,-4 L-4,-4 L4,4 L8,4 L8,12 M-4,-4 L-8,4 M4,4 L8,-4 M0,12 L-4,15 L4,15 Z M-6,16 L6,16 M-4,17 L4,17",

    // Rapid shutdown device: Emergency disconnect with indicator
    rapid_shutdown_device: "M-12,-12 L12,-12 L12,12 L-12,12 Z M-8,-8 L8,8 M-8,8 L8,-8 M0,-15 L0,-12 M0,12 L0,15 M-15,0 L-12,0 M12,0 L15,0",

    // === DISTRIBUTION EQUIPMENT ===
    // PV Module: Solar cell with grid lines (enhanced)
    module: "M-14,-9 L14,-9 L14,9 L-14,9 Z M-14,-3 L14,-3 M-14,3 L14,3 M-7,-9 L-7,9 M0,-9 L0,9 M7,-9 L7,9 M14,0 L17,0 M-14,0 L-17,0",

    // String: Series of connected PV modules
    string: "M-15,-6 L-9,-6 L-9,6 L-15,6 Z M-9,0 L-3,0 M-3,-6 L3,-6 L3,6 L-3,6 Z M3,0 L9,0 M9,-6 L15,-6 L15,6 L9,6 Z M-12,-3 L-12,3 M0,-3 L0,3 M12,-3 L12,3",

    // Combiner box: Multiple inputs converging to single output
    combiner_box: "M-12,-14 L12,-14 L12,14 L-12,14 Z M-8,-14 L-8,-8 M0,-14 L0,-8 M8,-14 L8,-8 M-8,-8 L0,2 L8,-8 M0,2 L0,14 M-6,-11 L-6,-8 M6,-11 L6,-8",

    // Junction box: Connection point with multiple terminals
    junction_box: "M-10,-10 L10,-10 L10,10 L-10,10 Z M0,0 L0,-10 M0,0 L0,10 M0,0 L-10,0 M0,0 L10,0 M0,0 L-7,-7 M0,0 L7,-7 M0,0 L-7,7 M0,0 L7,7",

    // Panelboard: Distribution panel with multiple circuits
    panelboard: "M-14,-14 L14,-14 L14,14 L-14,14 Z M-14,-7 L14,-7 M-14,0 L14,0 M-14,7 L14,7 M-10,-10 L-6,-10 L-4,-7 M-10,-3 L-6,-3 L-4,0 M-10,4 L-6,4 L-4,7 M10,-10 L6,-10 L4,-7 M10,-3 L6,-3 L4,0 M10,4 L6,4 L4,7",

    // Switchboard LV: Low voltage distribution board
    switchboard_lv: "M-16,-12 L16,-12 L16,12 L-16,12 Z M-16,0 L16,0 M-10,-8 L-6,-8 L-4,-4 M-10,-4 L-10,-8 M2,-8 L6,-8 L8,-4 M2,-4 L2,-8 M-10,4 L-6,4 L-4,8 M-10,8 L-10,4 M2,4 L6,4 L8,8 M2,8 L2,4",

    // Switchgear MV: Medium voltage switchgear with enclosure
    switchgear_mv: "M-14,-14 L14,-14 L14,14 L-14,14 Z M-10,-10 L10,-10 L10,10 L-10,10 Z M-6,0 L0,0 L4,-6 M0,0 L0,6 M-6,6 L6,6 M-14,0 L-10,0 M10,0 L14,0",

    // === METERING ===
    // Revenue meter: Utility meter with LCD display and kWh indicator
    revenue_meter: "M-12,-14 L12,-14 L12,14 L-12,14 Z M-9,-11 L9,-11 L9,-1 L-9,-1 Z M-7,-9 L-7,-3 M-4,-9 L-4,-3 M-1,-9 L-1,-3 M2,-9 L2,-3 M5,-9 L5,-3 M-6,3 L-6,10 L-2,10 L-2,3 Z M2,3 L2,10 L6,10 L6,3 Z M-4,5 L-4,8 M4,5 L4,8",

    // === MONITORING & WEATHER ===
    // Gateway: Network communication symbol
    gateway: "M-12,-8 L12,-8 L12,8 L-12,8 Z M-8,-4 L-8,4 M-4,-4 L-4,4 M0,-4 L0,4 M4,-4 L4,4 M8,-4 L8,4 M-14,0 L-12,0 M12,0 L14,0 M0,-8 L0,-12 M-3,-10 L0,-12 L3,-10",

    // Weather station: Combined meteorological symbol
    weather_station: "M-12,-12 L12,-12 L12,12 L-12,12 Z M-8,-8 L-4,-8 M-6,-10 L-6,-6 M6,-6 A3,3 0 1,1 6,0 M4,-4 L8,-4 M4,-2 L8,-2 M-6,4 Q-4,8 -2,4 Q0,8 2,4 Q4,8 6,4",

    // Pyranometer: Solar radiation sensor dome
    pyranometer: "M-10,4 A10,10 0 0,1 10,4 L10,8 L-10,8 Z M0,-8 L0,-2 M-6,-6 L-3,-3 M6,-6 L3,-3 M-8,-2 L-4,0 M8,-2 L4,0 M0,4 L0,8",

    // Ambient temperature sensor: Thermometer symbol
    ambient_temp_sensor: "M-3,-14 L3,-14 L3,6 A5,5 0 1,1 -3,6 Z M0,-10 L0,4 M-2,6 A3,3 0 1,0 2,6 A3,3 0 1,0 -2,6",

    // Module temperature sensor: Thermometer with panel
    module_temp_sensor: "M-14,-6 L6,-6 L6,6 L-14,6 Z M-14,0 L6,0 M-4,-6 L-4,6 M10,-10 L14,-10 L14,8 A3,3 0 1,1 10,8 Z M12,-6 L12,6",

    // Anemometer: Cup anemometer symbol
    anemometer: "M0,-2 L0,14 M-2,14 L2,14 M0,-2 L-10,-8 M0,-2 L10,-8 M0,-2 L0,-14 M-10,-8 A4,4 0 1,1 -10,-4 M10,-8 A4,4 0 1,0 10,-4 M0,-14 A4,4 0 1,1 0,-10",

    // Rain gauge: Precipitation measurement bucket
    rain_gauge: "M-8,-10 L-10,10 L10,10 L8,-10 Z M-6,-6 L-7,6 M6,-6 L7,6 M-4,-14 L-4,-10 M0,-16 L0,-10 M4,-14 L4,-10 M-6,2 L-4,4 M0,0 L2,2 M6,-2 L4,0",

    // === INFRASTRUCTURE ===
    // Grounding system: Earth ground with multiple rods
    grounding_system: "M0,-10 L0,0 M-10,0 L10,0 M-8,3 L8,3 M-6,6 L6,6 M-4,9 L4,9 M-12,0 L-12,10 M12,0 L12,10 M0,6 L0,14",

    // Racking system: Structural support frame
    racking_system: "M-14,-8 L-14,10 M14,-8 L14,10 M-14,10 L14,10 M-14,-8 L14,-8 M-14,-8 L-10,-14 L10,-14 L14,-8 M-10,-14 L-10,-8 M10,-14 L10,-8 M0,-8 L0,10 M-7,-8 L-7,10 M7,-8 L7,10",

    // Tracker controller: Motor with directional sun tracking
    tracker_controller: "M-6,-12 A6,6 0 1,1 6,-12 A6,6 0 1,1 -6,-12 M0,-6 L0,4 M-8,4 L8,4 L8,12 L-8,12 Z M-4,4 L-4,12 M4,4 L4,12 M-12,-12 L-8,-12 M-10,-14 L-12,-12 L-10,-10 M12,-12 L8,-12 M10,-14 L12,-12 L10,-10",

    // === DEFAULT ===
    // Default: Simple circle for unknown types
    default: "M0,0 m-10,0 a10,10 0 1,0 20,0 a10,10 0 1,0 -20,0"
};

export const getSymbol = (type: string) => {
    switch (type) {
        // Grid & Utility
        case 'grid_connection_point': return SYMBOLS.grid;
        case 'grid': return SYMBOLS.grid;

        // Transformers
        case 'transformer': return SYMBOLS.transformer;
        case 'control_transformer': return SYMBOLS.control_transformer;
        case 'current_transformer': return SYMBOLS.current_transformer;
        case 'voltage_transformer': return SYMBOLS.voltage_transformer;

        // Power Conversion
        case 'inverter': return SYMBOLS.inverter;
        case 'pcs_bidirectional': return SYMBOLS.pcs_bidirectional;
        case 'mppt': return SYMBOLS.mppt;
        case 'dc_optimizer': return SYMBOLS.dc_optimizer;
        case 'microinverter': return SYMBOLS.microinverter;

        // Protection Devices
        case 'circuit_breaker': return SYMBOLS.breaker;
        case 'fuse': return SYMBOLS.fuse;
        case 'disconnect_switch': return SYMBOLS.switch;
        case 'protective_relay': return SYMBOLS.protective_relay;
        case 'surge_protection_device': return SYMBOLS.surge_protection_device;
        case 'rapid_shutdown_device': return SYMBOLS.rapid_shutdown_device;

        // Distribution Equipment
        case 'pv_module': return SYMBOLS.module;
        case 'string': return SYMBOLS.string;
        case 'combiner_box': return SYMBOLS.combiner_box;
        case 'junction_box': return SYMBOLS.junction_box;
        case 'panelboard': return SYMBOLS.panelboard;
        case 'switchboard_lv': return SYMBOLS.switchboard_lv;
        case 'switchgear_mv': return SYMBOLS.switchgear_mv;

        // Metering
        case 'revenue_meter': return SYMBOLS.revenue_meter;

        // Monitoring & Weather
        case 'gateway': return SYMBOLS.gateway;
        case 'weather_station': return SYMBOLS.weather_station;
        case 'pyranometer': return SYMBOLS.pyranometer;
        case 'ambient_temp_sensor': return SYMBOLS.ambient_temp_sensor;
        case 'module_temp_sensor': return SYMBOLS.module_temp_sensor;
        case 'anemometer': return SYMBOLS.anemometer;
        case 'rain_gauge': return SYMBOLS.rain_gauge;

        // Infrastructure
        case 'grounding_system': return SYMBOLS.grounding_system;
        case 'racking_system': return SYMBOLS.racking_system;
        case 'tracker_controller': return SYMBOLS.tracker_controller;

        default: return SYMBOLS.default;
    }
};

// Helper to determine hierarchy level (Fallback for disconnected nodes)
// Also used for sorting types in the Details Panel
// Levels represent typical position in solar farm single-line diagram (0 = grid, higher = field)
export const getNodeTierFallback = (type: string): number => {
    switch (type) {
        // === UTILITY INTERCONNECTION (Tier 0-1) ===
        case 'grid_connection_point': return 0;
        case 'grid': return 0;
        case 'revenue_meter': return 1;

        // === MEDIUM VOLTAGE (Tier 1-2) ===
        case 'switchgear_mv': return 1;
        case 'protective_relay': return 1.5;
        case 'surge_protection_device': return 1.5;

        // === TRANSFORMATION (Tier 2) ===
        case 'transformer': return 2;
        case 'voltage_transformer': return 2;
        case 'current_transformer': return 2;
        case 'control_transformer': return 2.5;

        // === LOW VOLTAGE DISTRIBUTION (Tier 3) ===
        case 'switchboard_lv': return 3;
        case 'panelboard': return 3;
        case 'disconnect_switch': return 3.5;

        // === PROTECTION & SWITCHING (Tier 4) ===
        case 'circuit_breaker': return 4;
        case 'rapid_shutdown_device': return 4;
        case 'grounding_system': return 4;

        // === POWER CONVERSION (Tier 5) ===
        case 'inverter': return 5;
        case 'pcs_bidirectional': return 5;
        case 'microinverter': return 5;

        // === DC COLLECTION (Tier 6) ===
        case 'mppt': return 6;
        case 'dc_optimizer': return 6;
        case 'combiner_box': return 6.5;

        // === STRING LEVEL (Tier 7) ===
        case 'fuse': return 7;
        case 'junction_box': return 7;

        // === MODULE LEVEL (Tier 8-9) ===
        case 'string': return 8;
        case 'pv_module': return 9;
        case 'racking_system': return 9;
        case 'tracker_controller': return 8.5;

        // === COMMUNICATIONS HIERARCHY (parallels power hierarchy) ===
        // Tier 1: Site-level supervisory control
        case 'scada': return 1;
        case 'control_system': return 1;
        case 'hmi': return 1;

        // Tier 2: Site-level data aggregation
        case 'gateway': return 2;
        case 'rtu': return 2;
        case 'plc': return 2;

        // Tier 3: Intermediate data collection
        case 'data_logger': return 3;
        case 'das': return 3;
        case 'communication_hub': return 3;

        // Tier 4: Monitoring stations
        case 'weather_station': return 4;
        case 'meter': return 4;
        case 'power_meter': return 4;

        // Tier 5: Field-level aggregation
        case 'sensor_hub': return 5;
        case 'repeater': return 5;

        // Tier 6: Environmental sensors (site-level)
        case 'pyranometer': return 6;
        case 'anemometer': return 6;
        case 'rain_gauge': return 6;

        // Tier 7: Ambient sensors
        case 'ambient_temp_sensor': return 7;
        case 'humidity_sensor': return 7;
        case 'barometer': return 7;

        // Tier 8: Equipment-level sensors
        case 'module_temp_sensor': return 8;
        case 'current_sensor': return 8;
        case 'voltage_sensor': return 8;

        // Tier 9: Generic/unclassified sensors
        case 'sensor': return 9;

        default: return 10;
    }
};

export const getTypeTier = (type: string): number => {
    return getNodeTierFallback(type);
};

// Communications hierarchy tier for ancillary nodes (1-9)
// Used for positioning ancillary nodes in their own section of the canvas
export const getCommsTier = (type: string): number => {
    switch (type) {
        // Tier 1: Site-level supervisory control
        case 'scada': return 1;
        case 'control_system': return 1;
        case 'hmi': return 1;

        // Tier 2: Site-level data aggregation
        case 'gateway': return 2;
        case 'rtu': return 2;
        case 'plc': return 2;

        // Tier 3: Intermediate data collection
        case 'data_logger': return 3;
        case 'das': return 3;
        case 'communication_hub': return 3;

        // Tier 4: Monitoring stations
        case 'weather_station': return 4;
        case 'meter': return 4;
        case 'power_meter': return 4;

        // Tier 5: Field-level aggregation
        case 'sensor_hub': return 5;
        case 'repeater': return 5;

        // Tier 6: Environmental sensors
        case 'pyranometer': return 6;
        case 'anemometer': return 6;
        case 'rain_gauge': return 6;

        // Tier 7: Ambient sensors
        case 'ambient_temp_sensor': return 7;
        case 'humidity_sensor': return 7;
        case 'barometer': return 7;

        // Tier 8: Equipment-level sensors
        case 'module_temp_sensor': return 8;
        case 'current_sensor': return 8;
        case 'voltage_sensor': return 8;

        // Tier 9: Generic sensors
        case 'sensor': return 9;

        // Default for unknown comms types
        default: return 5;
    }
};

// Check if a type is an ancillary/comms type
export const isAncillaryType = (type: string): boolean => {
    const ancillaryTypes = [
        'scada', 'control_system', 'hmi',
        'gateway', 'rtu', 'plc',
        'data_logger', 'das', 'communication_hub',
        'weather_station', 'meter', 'power_meter',
        'sensor_hub', 'repeater',
        'pyranometer', 'anemometer', 'rain_gauge',
        'ambient_temp_sensor', 'humidity_sensor', 'barometer',
        'module_temp_sensor', 'current_sensor', 'voltage_sensor',
        'sensor'
    ];
    return ancillaryTypes.includes(type) || type.includes('comm') || type.includes('monitor');
};

export const processData = (data: PVSiteData): { nodes: GraphNode[], links: GraphLink[] } => {
    const nodesData = data.nodes;
    const edgesData = data.edges;

    // 1. Build Hierarchy from Connections (Target -> Source = Parent -> Child)
    const hierarchyAdj = new Map<string, string[]>();
    const hasParent = new Set<string>();

    // Filter for structural edges (Anything that carries power: AC, DC, or generic 'connects')
    // We explicitly exclude COMMs as they don't define the power hierarchy.
    const structuralEdges = edgesData.filter(e => e.signal !== 'COMM');

    structuralEdges.forEach(edge => {
        // Target is Parent, Source is Child
        if (!hierarchyAdj.has(edge.target)) hierarchyAdj.set(edge.target, []);
        hierarchyAdj.get(edge.target)?.push(edge.source);
        hasParent.add(edge.source);
    });

    // 2. Calculate Depths via Longest Path (Relaxation)
    // This ensures that if A->B->C exists, C is at least depth 2, even if A->C also exists.
    const nodeDepths = new Map<string, number>();

    // Initialize roots (nodes with no structural parent)
    const roots: string[] = [];
    nodesData.forEach(n => {
        const id = n["Primary ID"];
        if (!hasParent.has(id)) {
            nodeDepths.set(id, 0);
            roots.push(id);
        }
    });

    // Iterative Relaxation (Bellman-Ford style for DAGs)
    // We iterate |V| times (or until convergence) to propagate depths.
    // Since we want LONGEST path, we maximize depth.
    let changed = true;
    let iterations = 0;
    const maxIterations = nodesData.length + 1; // Safety break for cycles

    while (changed && iterations < maxIterations) {
        changed = false;
        iterations++;

        structuralEdges.forEach(edge => {
            const parentDepth = nodeDepths.get(edge.target);
            if (parentDepth !== undefined) {
                const currentChildDepth = nodeDepths.get(edge.source) || -1;
                const newChildDepth = parentDepth + 1;

                if (newChildDepth > currentChildDepth) {
                    nodeDepths.set(edge.source, newChildDepth);
                    changed = true;
                }
            }
        });
    }

    if (iterations >= maxIterations) {
        console.warn('[graphUtils] Cycle detected or max iterations reached in depth calculation');
    }

    // 3. Create Graph Nodes
    const nodes: GraphNode[] = nodesData.map(node => {
        const type = node.component_type;
        const id = node["Primary ID"];
        const label = type === 'pv_module' ? '' : id;

        // Determine Weight (Size of node) - based on component importance in single-line diagram
        let val = 5;
        // Utility Interconnection
        if (type === 'grid_connection_point' || type === 'grid') val = 40;
        else if (type === 'revenue_meter') val = 35;
        // Medium Voltage
        else if (type === 'switchgear_mv') val = 35;
        else if (type === 'protective_relay') val = 18;
        else if (type === 'surge_protection_device') val = 16;
        // Transformers
        else if (type === 'transformer') val = 30;
        else if (type === 'voltage_transformer') val = 22;
        else if (type === 'current_transformer') val = 22;
        else if (type === 'control_transformer') val = 18;
        // Low Voltage Distribution
        else if (type === 'switchboard_lv') val = 28;
        else if (type === 'panelboard') val = 25;
        else if (type === 'disconnect_switch') val = 22;
        // Protection & Switching
        else if (type === 'circuit_breaker') val = 15;
        else if (type === 'rapid_shutdown_device') val = 14;
        else if (type === 'grounding_system') val = 16;
        // Power Conversion
        else if (type === 'inverter') val = 20;
        else if (type === 'pcs_bidirectional') val = 22;
        else if (type === 'microinverter') val = 10;
        // DC Collection
        else if (type === 'mppt') val = 12;
        else if (type === 'dc_optimizer') val = 10;
        else if (type === 'combiner_box') val = 14;
        // String Level
        else if (type === 'fuse') val = 8;
        else if (type === 'junction_box') val = 10;
        // Module Level
        else if (type === 'string') val = 6;
        else if (type === 'pv_module') val = 4;
        else if (type === 'racking_system') val = 8;
        else if (type === 'tracker_controller') val = 12;
        // Monitoring & Weather
        else if (type === 'gateway') val = 15;
        else if (type === 'weather_station') val = 14;
        else if (type === 'pyranometer') val = 10;
        else if (type === 'anemometer') val = 10;
        else if (type === 'rain_gauge') val = 10;
        else if (type === 'ambient_temp_sensor') val = 8;
        else if (type === 'module_temp_sensor') val = 8;
        else if (type === 'sensor') val = 10;

        // Determine if this is an ancillary/DAS node (not part of main power hierarchy)
        // Ordered by communications hierarchy: SCADA -> Gateway -> DataLogger -> Stations -> Sensors
        const ancillaryTypes = [
            // Tier 1: Site-level supervisory control
            'scada', 'control_system', 'hmi',
            // Tier 2: Site-level data aggregation
            'gateway', 'rtu', 'plc',
            // Tier 3: Intermediate data collection
            'data_logger', 'das', 'communication_hub',
            // Tier 4: Monitoring stations
            'weather_station', 'meter', 'power_meter',
            // Tier 5: Field-level aggregation
            'sensor_hub', 'repeater',
            // Tier 6: Environmental sensors
            'pyranometer', 'anemometer', 'rain_gauge',
            // Tier 7: Ambient sensors
            'ambient_temp_sensor', 'humidity_sensor', 'barometer',
            // Tier 8: Equipment-level sensors
            'module_temp_sensor', 'current_sensor', 'voltage_sensor',
            // Tier 9: Generic sensors
            'sensor'
        ];
        const isAncillary = ancillaryTypes.includes(type) || type.includes('comm') || type.includes('monitor');

        // Determine Tier
        // Use calculated depth if available, otherwise fallback (for disconnected nodes)
        let tier = nodeDepths.get(id);
        if (tier === undefined) {
            tier = getNodeTierFallback(type);
        }

        // Find structural parent for grouping
        // We prefer the parent that gave us the max depth (critical path parent)
        // But for simplicity, any structural parent is fine for grouping.
        // Let's try to find the parent with depth = tier - 1
        let parentId: string | undefined = undefined;
        const potentialParents = structuralEdges.filter(e => e.source === id).map(e => e.target);

        if (potentialParents.length > 0) {
            // Pick the parent that is logically "above" us (tier - 1)
            parentId = potentialParents.find(pid => {
                const pDepth = nodeDepths.get(pid);
                return pDepth !== undefined && pDepth === (tier! - 1);
            });
            // Fallback to first parent if no exact tier match (e.g. skip-level connection)
            if (!parentId) parentId = potentialParents[0];
        }

        return {
            id: id,
            label: label,
            type: type,
            color: ComponentColors[type] || ComponentColors.default,
            group: type,
            details: node,
            val,
            tier: tier,
            siblingIndex: 0,
            groupIndex: 0,
            parentId: parentId,
            isAncillary: isAncillary
        };
    });

    // 4. Calculate Grid Topology (Siblings)
    const siblingsGroups = new Map<string, GraphNode[]>();
    nodes.forEach(node => {
        const pId = node.parentId || 'root';
        const key = `${pId}_${node.type}`;

        if (!siblingsGroups.has(key)) siblingsGroups.set(key, []);
        siblingsGroups.get(key)?.push(node);
    });

    siblingsGroups.forEach((group) => {
        group.sort((a, b) => a.id.localeCompare(b.id));
        group.forEach((node, index) => {
            node.siblingIndex = index;
            node.groupIndex = index;
        });
    });

    // 5. Create Links
    const links: GraphLink[] = edgesData.map(edge => {
        let color = '#6B7280'; // Gray
        if (edge.signal === 'AC') color = '#EF4444'; // Red
        if (edge.signal === 'DC') color = '#10B981'; // Green
        if (edge.signal === 'COMM') color = '#3B82F6'; // Blue

        return {
            source: edge.source,
            target: edge.target,
            label: edge.relation,
            color: color,
            details: edge
        };
    });

    // 6. Calculate Power Flow (Initial Calculation)
    calculatePowerFlow(nodes);

    return { nodes, links };
};

export const getChildren = (nodeId: string, links: GraphLink[]): string[] => {
    return links
        .filter(link => link.target === nodeId && link.details.signal !== 'COMM')
        .map(link => link.source);
};

export const getDescendants = (nodeId: string, links: GraphLink[]): { nodeIds: Set<string>, links: GraphLink[] } => {
    const descendants = new Set<string>();
    const descendantLinks: GraphLink[] = [];
    const queue = [nodeId];

    while (queue.length > 0) {
        const currentId = queue.shift()!;
        const childrenLinks = links.filter(l => l.target === currentId && l.details.signal !== 'COMM');

        childrenLinks.forEach(link => {
            if (!descendants.has(link.source)) {
                descendants.add(link.source);
                queue.push(link.source);
                descendantLinks.push(link);
            }
        });

        // Safety break for extremely large or cyclic graphs (though Set handles simple cycles)
        if (descendants.size > 10000) {
            console.warn('[graphUtils] Safety limit reached in getDescendants');
            break;
        }
    }

    return { nodeIds: descendants, links: descendantLinks };
};

export const getLinkedComms = (nodeId: string, links: GraphLink[]): Set<string> => {
    const commsNodes = new Set<string>();
    const queue = [nodeId];
    const visited = new Set<string>([nodeId]);

    while (queue.length > 0) {
        const currentId = queue.shift()!;
        // Find any link that touches this node and is COMM
        const commLinks = links.filter(l =>
            (l.source === currentId || l.target === currentId) &&
            l.details.signal === 'COMM'
        );

        commLinks.forEach(link => {
            const neighbor = link.source === currentId ? link.target : link.source;
            if (!visited.has(neighbor)) {
                visited.add(neighbor);
                commsNodes.add(neighbor);
                queue.push(neighbor);
            }
        });
    }

    return commsNodes;
}

export interface HierarchyNode {
    name: string;
    details: GraphNode | null;
    children?: HierarchyNode[];
}

export const buildHierarchy = (nodes: GraphNode[], links: GraphLink[]): HierarchyNode => {
    const nodeMap = new Map<string, HierarchyNode>();

    nodes.forEach(n => {
        nodeMap.set(n.id, { name: n.id, details: n, children: [] });
    });

    const roots = new Set<string>(nodes.map(n => n.id));

    links.forEach(link => {
        const parent = nodeMap.get(link.target);
        const child = nodeMap.get(link.source);

        if (parent && child && link.details.signal !== 'COMM') {
            parent.children = parent.children || [];
            parent.children.push(child);
            roots.delete(link.source);
        }
    });

    if (roots.size === 0) return { name: "Empty", details: null, children: [] };

    const rootChildren = Array.from(roots).map(id => nodeMap.get(id)!);
    rootChildren.sort((a, b) => (a.details?.tier || 0) - (b.details?.tier || 0));

    if (rootChildren.length === 1) return rootChildren[0];

    return {
        name: "Site Root",
        details: null,
        children: rootChildren
    };
};

/**
 * Calculates cumulative power for all nodes in the graph.
 * Traverses bottom-up from modules to grid.
 * Updates nodes in-place with `cumulativePower` and `normalizedPower`.
 */
export const calculatePowerFlow = (nodes: GraphNode[]): void => {
    // Initialize power with module values
    const nodeMap = new Map<string, GraphNode>();
    nodes.forEach(n => {
        nodeMap.set(n.id, n);
        if (n.type === 'pv_module') {
            // Try to get power from properties, fallback to 0
            const watts = Number(n.details.properties?.["STC Power (W)"] || n.details.properties?.["Power"] || 0);
            n.cumulativePower = watts;
        } else {
            n.cumulativePower = 0;
        }
    });

    // Bottom-up aggregation using depth
    // Sort nodes by depth descending (deepest first)
    const sortedByDepth = [...nodes].sort((a, b) => b.tier - a.tier);

    sortedByDepth.forEach(node => {
        if (node.parentId) {
            const parent = nodeMap.get(node.parentId);
            if (parent) {
                parent.cumulativePower = (parent.cumulativePower || 0) + (node.cumulativePower || 0);
            }
        }
    });

    // Normalize Power per Tier and Assign Colors
    const tierStats = new Map<number, { min: number, max: number }>();

    // First pass: find min/max per tier
    nodes.forEach(n => {
        if (!tierStats.has(n.tier)) {
            tierStats.set(n.tier, { min: Infinity, max: -Infinity });
        }
        const stats = tierStats.get(n.tier)!;
        const power = n.cumulativePower || 0;
        if (power > 0) { // Only consider nodes with power for scaling
            stats.min = Math.min(stats.min, power);
            stats.max = Math.max(stats.max, power);
        }
    });

    // Second pass: normalize and assign color
    nodes.forEach(n => {
        const stats = tierStats.get(n.tier)!;
        const power = n.cumulativePower || 0;
        const baseColor = ComponentColors[n.type] || ComponentColors.default;

        // Determine if Ancillary
        // Ancillary = Explicit types OR No Power Flow (and not a structural component that might just be open)
        const ancillaryTypes = new Set([
            'gateway', 'sensor', 'weather_station', 'data_logger', 'meter',
            'pyranometer', 'anemometer', 'rain_gauge', 'ambient_temp_sensor', 'module_temp_sensor'
        ]);
        const structuralTypes = new Set([
            // Grid & Utility
            'grid_connection_point', 'grid', 'revenue_meter',
            // Transformers
            'transformer', 'control_transformer', 'current_transformer', 'voltage_transformer',
            // Protection & Distribution
            'switchgear_mv', 'switchboard_lv', 'panelboard', 'disconnect_switch',
            'circuit_breaker', 'fuse', 'protective_relay', 'surge_protection_device', 'rapid_shutdown_device',
            // Power Conversion
            'inverter', 'pcs_bidirectional', 'microinverter', 'mppt', 'dc_optimizer',
            // DC Collection
            'combiner_box', 'junction_box',
            // Module Level
            'string', 'pv_module', 'racking_system', 'tracker_controller',
            // Infrastructure
            'grounding_system'
        ]);

        if (ancillaryTypes.has(n.type)) {
            n.isAncillary = true;
        } else if (power === 0 && !structuralTypes.has(n.type)) {
            // If it's not a known structural type and has no power, treat as ancillary
            n.isAncillary = true;
        } else {
            n.isAncillary = false;
        }

        if (power === 0) {
            n.normalizedPower = 0;
            // If no power flow (e.g. comms only), use base color but slightly dimmed to differentiate
            n.color = getPowerAdjustedColor(baseColor, 0.5);
        } else if (stats.max === stats.min) {
            // If only one item or all same power, treat as full power (1.0)
            n.normalizedPower = 1.0;
            n.color = getPowerAdjustedColor(baseColor, 1.0);
        } else {
            // Normalize to 0..1
            const rawNormal = (power - stats.min) / (stats.max - stats.min);

            // Map 0..1 to 0.3..1.0 to ensure lowest power is still visible (not black/invisible)
            n.normalizedPower = 0.3 + (rawNormal * 0.7);

            n.color = getPowerAdjustedColor(baseColor, n.normalizedPower);
        }
    });
};

// Helper to adjust color based on power (Hue shift + Lightness)
const getPowerAdjustedColor = (baseColor: string, t: number): string => {
    // Parse hex to rgb
    let r = 0, g = 0, b = 0;
    if (baseColor.startsWith('#')) {
        const hex = baseColor.substring(1);
        if (hex.length === 3) {
            r = parseInt(hex[0] + hex[0], 16);
            g = parseInt(hex[1] + hex[1], 16);
            b = parseInt(hex[2] + hex[2], 16);
        } else if (hex.length === 6) {
            r = parseInt(hex.substring(0, 2), 16);
            g = parseInt(hex.substring(2, 4), 16);
            b = parseInt(hex.substring(4, 6), 16);
        }
    }

    // Convert RGB to HSL for better manipulation
    const rNorm = r / 255, gNorm = g / 255, bNorm = b / 255;
    const max = Math.max(rNorm, gNorm, bNorm), min = Math.min(rNorm, gNorm, bNorm);
    let h = 0, s = 0, l = (max + min) / 2;

    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case rNorm: h = (gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0); break;
            case gNorm: h = (bNorm - rNorm) / d + 2; break;
            case bNorm: h = (rNorm - gNorm) / d + 4; break;
        }
        h /= 6;
    }

    // Apply Power Transformations
    // t = 0 (Low Power): Lighter (+20% Lightness), Shift Hue -15deg
    // t = 1 (High Power): Darker (-10% Lightness), Shift Hue +15deg

    // Hue Shift (in degrees, normalized 0-1)
    const hueShift = (t - 0.5) * 30; // -15 to +15 degrees
    let newH = (h * 360 + hueShift) % 360;
    if (newH < 0) newH += 360;
    newH /= 360;

    // Lightness Shift
    // Map t(0..1) to Lightness offset (+0.2 .. -0.1)
    const lOffset = 0.2 - (t * 0.3);
    let newL = Math.max(0, Math.min(1, l + lOffset));

    // Convert back to RGB
    const hue2rgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    };

    let q = newL < 0.5 ? newL * (1 + s) : newL + s - newL * s;
    let p = 2 * newL - q;

    const finalR = Math.round(hue2rgb(p, q, newH + 1 / 3) * 255);
    const finalG = Math.round(hue2rgb(p, q, newH) * 255);
    const finalB = Math.round(hue2rgb(p, q, newH - 1 / 3) * 255);

    return `rgb(${finalR}, ${finalG}, ${finalB})`;
};

export const calculateExpansionForType = (
    nodes: GraphNode[],
    links: GraphLink[],
    currentExpanded: Set<string>,
    sourceNodeIds: string[],
    targetType: string
): Set<string> => {
    const newExpanded = new Set(currentExpanded);
    const nodeMap = new Map(nodes.map(n => [n.id, n]));

    // 1. Find all descendants of targetType
    const targetNodes: GraphNode[] = [];
    sourceNodeIds.forEach(sourceId => {
        const { nodeIds } = getDescendants(sourceId, links);
        nodeIds.forEach(id => {
            const node = nodeMap.get(id);
            if (node && node.type === targetType) {
                targetNodes.push(node);
            }
        });
    });

    if (targetNodes.length === 0) return newExpanded;

    // 2. Identify parents
    const parentIds = new Set<string>();
    targetNodes.forEach(n => {
        if (n.parentId) parentIds.add(n.parentId);
    });

    // 3. Check if ANY parent is expanded
    const anyParentExpanded = Array.from(parentIds).some(pid => currentExpanded.has(pid));

    if (anyParentExpanded) {
        // COLLAPSE: Remove ALL parents
        parentIds.forEach(pid => newExpanded.delete(pid));
    } else {
        // EXPAND: Add parents and their ancestors
        parentIds.forEach(pid => {
            newExpanded.add(pid);
            let curr: GraphNode | undefined = nodeMap.get(pid);
            while (curr && curr.parentId) {
                const parent = nodeMap.get(curr.parentId);
                if (parent) {
                    newExpanded.add(parent.id);
                    curr = parent;
                } else {
                    break;
                }
            }
        });
        // Also ensure source nodes are expanded
        sourceNodeIds.forEach(id => newExpanded.add(id));
    }

    return newExpanded;
};

export const getVisibleNodes = (
    nodes: GraphNode[],
    links: GraphLink[],
    expandedIds: Set<string>
): Set<string> => {
    // 1. Determine Visible Nodes (BFS)
    const visibleNodesMap = new Map<string, GraphNode>();
    const nodeMap = new Map(nodes.map(n => [n.id, n]));

    // Start with roots (Tier 0 or no parent)
    const queue = nodes.filter(n => n.tier === 0 || !n.parentId);
    const visited = new Set<string>();

    queue.forEach(n => visibleNodesMap.set(n.id, n));

    while (queue.length > 0) {
        const current = queue.shift()!;
        visited.add(current.id);
        const childrenIds = getChildren(current.id, links);

        if (childrenIds.length > 0 && expandedIds.has(current.id)) {
            childrenIds.forEach(childId => {
                const childNode = nodeMap.get(childId);
                if (childNode && !visited.has(childId)) {
                    visibleNodesMap.set(childId, childNode);
                    queue.push(childNode);
                }
            });
        }
    }

    return new Set(visibleNodesMap.keys());
};

export const getExpansionForTier = (
    nodes: GraphNode[],
    targetTier: number
): Set<string> => {
    const expanded = new Set<string>();

    nodes.forEach(node => {
        if (node.tier < targetTier) {
            expanded.add(node.id);
        }
    });

    return expanded;
};

export const getSingleBranchExpansion = (nodes: GraphNode[], links: GraphLink[]): Set<string> => {
    const expanded = new Set<string>();
    const nodeMap = new Map(nodes.map(n => [n.id, n]));

    // Find Root (Tier 0)
    const roots = nodes.filter(n => n.tier === 0);

    if (roots.length === 0) return expanded;

    // Pick the best root:
    // 1. Prioritize 'grid_connection_point'
    // 2. Fallback to highest cumulative power
    roots.sort((a, b) => {
        if (a.type === 'grid_connection_point' && b.type !== 'grid_connection_point') return -1;
        if (b.type === 'grid_connection_point' && a.type !== 'grid_connection_point') return 1;
        return (b.cumulativePower || 0) - (a.cumulativePower || 0);
    });

    let current = roots[0];
    let safety = 0;

    // Walk down, picking the child with the HIGHEST POWER at each step
    while (current && safety < 1000) {
        safety++;
        expanded.add(current.id);

        const childrenIds = getChildren(current.id, links);
        if (childrenIds.length > 0) {
            // Sort children by power descending
            childrenIds.sort((a, b) => {
                const nodeA = nodeMap.get(a);
                const nodeB = nodeMap.get(b);
                const powerA = nodeA?.cumulativePower || 0;
                const powerB = nodeB?.cumulativePower || 0;
                return powerB - powerA;
            });

            const firstChildId = childrenIds[0];

            // Cycle detection
            if (expanded.has(firstChildId)) {
                break;
            }

            const firstChild = nodeMap.get(firstChildId);

            if (firstChild) {
                current = firstChild;
            } else {
                break;
            }
        } else {
            break;
        }
    }

    if (safety >= 1000) {
        console.warn('[graphUtils] Safety limit reached in getSingleBranchExpansion');
    }

    return expanded;
};

/**
 * Get expansion for a full path from root (grid) to leaf (string/module) passing through a selected node.
 */
export const getPathExpansionFromNode = (
    selectedNodeId: string,
    nodes: GraphNode[],
    links: GraphLink[]
): Set<string> => {
    const expanded = new Set<string>();
    const nodeMap = new Map(nodes.map(n => [n.id, n]));
    const selectedNode = nodeMap.get(selectedNodeId);

    if (!selectedNode) return expanded;

    // 1. Walk UP from selected node to root, collecting all ancestors
    let current: GraphNode | undefined = selectedNode;

    while (current) {
        expanded.add(current.id);

        if (current.parentId) {
            current = nodeMap.get(current.parentId);
        } else {
            current = undefined;
        }
    }

    // 2. Walk DOWN from selected node to a leaf (deepest descendant in one branch)
    current = selectedNode;
    let safety = 0;

    while (current && safety < 1000) {
        safety++;
        expanded.add(current.id);

        const childrenIds = getChildren(current.id, links);
        if (childrenIds.length > 0) {
            // Sort children to be deterministic (pick first alphabetically)
            childrenIds.sort();
            const firstChildId = childrenIds[0];

            if (expanded.has(firstChildId)) {
                // Cycle detected
                break;
            }

            const firstChild = nodeMap.get(firstChildId);

            if (firstChild) {
                current = firstChild;
            } else {
                break;
            }
        } else {
            // Reached a leaf
            break;
        }
    }

    return expanded;
};

export const getExpansionToSeeType = (
    nodes: GraphNode[],
    _links: GraphLink[],
    targetType: string
): Set<string> => {
    const expanded = new Set<string>();
    const nodeMap = new Map(nodes.map(n => [n.id, n]));

    // 1. Find all nodes of the target type
    const targetNodes = nodes.filter(n => n.type === targetType);

    // 2. For each target node, walk up the hierarchy and expand all ancestors
    targetNodes.forEach(node => {
        let curr = node;
        let safety = 0;
        const path = new Set<string>();

        while (curr && curr.parentId && safety < 1000) {
            safety++;

            // Cycle detection in parent chain
            if (path.has(curr.parentId)) {
                // Cycle detected
                break;
            }
            path.add(curr.id);

            const parent = nodeMap.get(curr.parentId);
            if (parent) {
                expanded.add(parent.id);
                curr = parent;
            } else {
                break;
            }
        }
    });

    return expanded;
};
